import { useState, type FormEvent } from 'react';
import { useClerk, useSignIn } from '@clerk/clerk-react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  AuthCard,
  FormField,
  PasswordInput,
  VerificationCodeInput,
  SubmitButton,
  Button,
} from '@legacyorb/ui';
import { Input } from '@/components/ui/input';

type Step = 'credentials' | 'verify';

export function SignInPage() {
  const { signIn, setActive, isLoaded } = useSignIn();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Clerk production instances challenge sign-ins from unrecognized devices
  // with an email code second factor (device verification), even for users
  // with no MFA enrolled. Send the code and switch to the verify step.
  const startEmailCodeVerification = async () => {
    const emailFactor = signIn?.supportedSecondFactors?.find((f) => f.strategy === 'email_code');
    if (!signIn || !emailFactor) {
      setError('Sign-in could not be completed. Please try again.');
      return;
    }
    await signIn.prepareSecondFactor({ strategy: 'email_code' });
    setCode('');
    setStep('verify');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    setError('');
    setIsSubmitting(true);

    const attempt = async () => {
      const result = await signIn.create({
        identifier: email,
        password,
      });

      if (result.status === 'complete' && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        navigate('/');
      } else if (result.status === 'needs_second_factor') {
        await startEmailCodeVerification();
      } else {
        setError('Sign-in could not be completed. Please try again.');
      }
    };

    try {
      await attempt();
    } catch (err: unknown) {
      const clerkError = err as { errors?: Array<{ code?: string; message: string }> };
      const errorCode = clerkError.errors?.[0]?.code;

      if (errorCode === 'session_exists') {
        try {
          await signOut();
          await attempt();
          return;
        } catch (retryErr: unknown) {
          const retryError = retryErr as { errors?: Array<{ message: string }> };
          setError(retryError.errors?.[0]?.message || 'Invalid email or password.');
          return;
        }
      }

      setError(clerkError.errors?.[0]?.message || 'Invalid email or password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    setError('');
    setIsSubmitting(true);

    try {
      const result = await signIn.attemptSecondFactor({
        strategy: 'email_code',
        code,
      });

      if (result.status === 'complete' && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        navigate('/');
      } else {
        setError('Sign-in could not be completed. Please try again.');
      }
    } catch (err: unknown) {
      const clerkError = err as { errors?: Array<{ code?: string; message: string }> };
      if (clerkError.errors?.[0]?.code === 'form_code_incorrect') {
        setError('Invalid verification code. Please check and try again.');
      } else {
        setError(clerkError.errors?.[0]?.message || 'Verification failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!isLoaded || !signIn) return;
    setError('');
    try {
      await signIn.prepareSecondFactor({ strategy: 'email_code' });
    } catch {
      setError('Could not resend the code. Please go back and sign in again.');
    }
  };

  const handleBack = () => {
    setError('');
    setCode('');
    setStep('credentials');
  };

  return (
    <div className="auth-theme flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-md px-4">
        <div className="mb-8 text-center">
          <div className="mb-4 flex justify-center">
            <img src="/logos/orb-mark.png" alt="LegacyOrb" className="h-12 w-12" />
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-foreground">
            Welcome Back
          </h1>
          <p className="mt-2 text-muted-foreground">Sign in to your account</p>
        </div>

        {step === 'verify' ? (
          <AuthCard title="Verify It's You" description={`Enter the 6-digit code sent to ${email}`}>
            <form onSubmit={handleVerify} className="space-y-6">
              <FormField label="Verification Code" htmlFor="code">
                <VerificationCodeInput
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </FormField>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <SubmitButton
                variant="brand"
                size="lg"
                isSubmitting={isSubmitting}
                loadingText="Verifying..."
                disabled={code.length !== 6}
              >
                Verify Code
              </SubmitButton>

              <p className="text-center text-sm text-muted-foreground">
                Didn't receive a code?{' '}
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-foreground underline-offset-4 hover:underline"
                  disabled={isSubmitting}
                >
                  Resend
                </button>
              </p>

              <Button type="button" variant="outline" className="w-full" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </form>
          </AuthCard>
        ) : (
          <AuthCard title="Sign In" description="Enter your email and password to continue">
            <form onSubmit={handleSubmit} className="space-y-6">
              <FormField label="Email" htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </FormField>

              <FormField label="Password" htmlFor="password">
                <PasswordInput
                  id="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  disabled={isSubmitting}
                />
              </FormField>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div id="clerk-captcha" />

              <SubmitButton
                variant="brand"
                size="lg"
                isSubmitting={isSubmitting}
                loadingText="Signing in..."
                disabled={!isLoaded}
              >
                Sign In
              </SubmitButton>

              <p className="text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                {/* New accounts are created inside the purchase funnel (step 1
                    of the stepper) — never the legacy standalone signup, which
                    skipped agreement/product/payment and stranded users. */}
                <Link
                  to="/start/account"
                  className="text-foreground underline-offset-4 hover:underline"
                >
                  Sign Up
                </Link>
              </p>
            </form>
          </AuthCard>
        )}
      </div>
    </div>
  );
}
